/**
 * Appwrite Backend Provider
 *
 * Combines all Appwrite services (Account, Databases, Storage) into a unified provider
 */

import { Client } from 'appwrite';
import { IBackendProvider } from '../interfaces/IBackendProvider';
import { AppwriteAuthService } from './AppwriteAuthService';
import { AppwriteDatabaseService } from './AppwriteDatabaseService';
import { AppwriteStorageService, StorageBucketIds } from './AppwriteStorageService';

export class AppwriteProvider implements IBackendProvider {
  public auth: AppwriteAuthService;
  public database: AppwriteDatabaseService;        // "brain" database for campaigns/leads
  public defaultDatabase: AppwriteDatabaseService; // Default database for users/credits
  public storage: AppwriteStorageService;

  private client: Client;
  private initialized = false;

  constructor(
    endpoint: string,
    projectId: string,
    brainDatabaseId: string,
    defaultDatabaseId: string,
    storageBuckets: StorageBucketIds,
    brainCollectionIds?: Record<string, string>,
    defaultCollectionIds?: Record<string, string>
  ) {
    // Initialize Appwrite client
    this.client = new Client();
    this.client.setEndpoint(endpoint).setProject(projectId);

    // Initialize storage first (needed by auth service)
    this.storage = new AppwriteStorageService(this.client, storageBuckets);

    // Initialize service adapters (auth needs storage for profile pictures)
    this.auth = new AppwriteAuthService(this.client, this.storage);
    this.database = new AppwriteDatabaseService(this.client, brainDatabaseId, brainCollectionIds);
    this.defaultDatabase = new AppwriteDatabaseService(this.client, defaultDatabaseId, defaultCollectionIds);
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      console.log('📱 Appwrite provider already initialized');
      return;
    }

    try {
      console.log('📱 Initializing Appwrite backend provider...');

      // Appwrite client is already initialized in constructor
      // Just mark as initialized
      this.initialized = true;

      console.log('✅ Appwrite backend provider initialized successfully');
    } catch (error: any) {
      console.error('❌ Failed to initialize Appwrite provider:', error);
      throw new Error(`Appwrite initialization failed: ${error.message}`);
    }
  }

  async disconnect(): Promise<void> {
    try {
      console.log('📱 Disconnecting Appwrite backend provider...');

      // Appwrite doesn't have an explicit disconnect method
      // Realtime subscriptions are cleaned up by their respective unsubscribe functions
      this.initialized = false;

      console.log('✅ Appwrite backend provider disconnected');
    } catch (error: any) {
      console.error('❌ Failed to disconnect Appwrite provider:', error);
      throw new Error(`Appwrite disconnect failed: ${error.message}`);
    }
  }

  getName(): string {
    return 'Appwrite';
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
